import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { formatPeriodLabel } from './trendTooltip'
import DetailSeriesPicker from './DetailSeriesPicker'
import { computeRangeTotal, rangeTotalLabel } from './computeRangeTotal'

// Detail table for the trend tool. Single-series at a time -- when
// `seriesList` carries multiple entries the table renders the one
// matching `detailSeriesKey` (defaulting to the first), and a
// DetailSeriesPicker above the table lets the user switch. This is the
// deliberate v1 trade-off from the multi-series spec: rather than try
// to combine N series of varied compounding semantics into a single
// table (which would silently mislead), surface one cleanly and let
// the user toggle.
//
// Footer + value-column label adapt to each series's effectiveMode
// (cumulative vs perPeriod). See computeRangeTotal.js for the matrix.

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
    overflow: 'auto',
    borderTop: '1px solid var(--opal-border-default, #eee)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12
  },
  th: {
    textAlign: 'left',
    padding: '6px 10px',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-700, #555)',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    borderBottom: '1px solid var(--opal-border-default, #eee)',
    background: 'var(--opal-surface-default, #fff)',
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  thRight: { textAlign: 'right' },
  modeSuffix: {
    textTransform: 'none',
    fontWeight: 'normal',
    color: 'var(--color-neutral-500, #888)',
    fontSize: 10,
    marginLeft: 4
  },
  td: {
    padding: '4px 10px',
    borderBottom: '1px solid var(--opal-border-default, #f3f3f3)',
    color: 'var(--color-neutral-800, #333)'
  },
  tdRight: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  },
  tdNegative: { color: 'var(--color-danger-600, #c62828)' },
  totalTr: {
    fontWeight: 'var(--font-weight-semibold, 600)',
    background: 'var(--theme-color-surface-d1, #f9f9f9)'
  },
  dash: { color: 'var(--color-neutral-300, #ccc)' }
}))

// Show a small mode badge in the value column header so the user
// knows what they're reading when a non-default mode is in effect.
// Always shown for the multi-series case where the user could be
// staring at any of N series with mixed modes.
function modeSuffixText (effectiveMode) {
  if (effectiveMode === 'cumulative') return 'cumulative'
  if (effectiveMode === 'perPeriod') return 'per period'
  return null
}

export default function TrendDetailTable ({ seriesList, frequency, detailSeriesKey, onDetailSeriesChange }) {
  const classes = useStyles()
  const { formatter: fmt } = useFormattingContext()

  // Memoize the normalized list so a non-array `seriesList` prop
  // doesn't produce a fresh `[]` literal each render and invalidate
  // the `active` memo below.
  const list = useMemo(() => Array.isArray(seriesList) ? seriesList : [], [seriesList])
  const showPicker = list.length > 1

  // Resolve the active series: prefer the explicit key, then fall back
  // to the first. Falling back rather than nulling out avoids a blank
  // table when the user removes the chip the picker was tracking.
  const active = useMemo(() => {
    if (!list.length) return null
    return list.find(s => s.id === detailSeriesKey) || list[0]
  }, [list, detailSeriesKey])

  if (!active) return null

  const points = active.points || []
  const valueFormat = active.valueFormat || null
  const effectiveMode = active.effectiveMode || null
  const total = computeRangeTotal(points, active.seriesType, effectiveMode)
  const totalLabel = rangeTotalLabel(active.seriesType, effectiveMode)
  const modeSuffix = modeSuffixText(effectiveMode)
  const valueHeader = active.name || 'Value'

  return (
    <div className={classes.wrapper}>
      {showPicker && (
        <DetailSeriesPicker
          items={list.map(s => ({ id: s.id, label: s.name }))}
          value={active.id}
          onChange={onDetailSeriesChange}
        />
      )}
      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.th}>Period</th>
            <th className={`${classes.th} ${classes.thRight}`}>
              {valueHeader}
              {modeSuffix && <span className={classes.modeSuffix}>({modeSuffix})</span>}
            </th>
          </tr>
        </thead>
        <tbody>
          {points.map(p => {
            const label = formatPeriodLabel(p.endDate, frequency)
            const isNeg = typeof p.value === 'number' && p.value < 0
            return (
              <tr key={`${p.startDate}_${p.endDate}`}>
                <td className={classes.td}>{label}</td>
                <td className={`${classes.td} ${classes.tdRight} ${isNeg ? classes.tdNegative : ''}`}>
                  {typeof p.value === 'number' ? fmt(p.value, valueFormat) : <span className={classes.dash}>--</span>}
                </td>
              </tr>
            )
          })}
          {points.length === 0 && (
            <tr>
              <td className={classes.td} colSpan={2}>
                <span className={classes.dash}>No data</span>
              </td>
            </tr>
          )}
        </tbody>
        {points.length > 0 && total != null && totalLabel && (
          <tfoot>
            <tr className={classes.totalTr}>
              <td className={classes.td}>{totalLabel}</td>
              <td className={`${classes.td} ${classes.tdRight} ${total < 0 ? classes.tdNegative : ''}`}>
                {fmt(total, valueFormat)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

TrendDetailTable.propTypes = {
  seriesList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    valueFormat: PropTypes.string,
    seriesType: PropTypes.string,
    effectiveMode: PropTypes.oneOf(['cumulative', 'perPeriod']),
    points: PropTypes.arrayOf(PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      value: PropTypes.number
    }))
  })).isRequired,
  frequency: PropTypes.string,
  detailSeriesKey: PropTypes.string,
  onDetailSeriesChange: PropTypes.func.isRequired
}
